import { useState, useEffect, useRef, Component, ReactNode } from 'react';
import './BandcampEmbed.css';
import { useMediaPlayerOptional } from '../contexts/MediaPlayerContext';

interface BandcampPreloadedEmbed {
  embedHtml: string;
  albumId?: string;
  trackId?: string;
  title?: string;
  artistName?: string;
}

interface BandcampEmbedProps {
  artistName: string;
  trackName: string;
  bandcampUrl?: string;
  preloadedEmbed?: BandcampPreloadedEmbed;
  onReady?: () => void;
  size?: 'small' | 'medium' | 'large';
}

interface BandcampOEmbed {
  found: boolean;
  html?: string;
  title?: string;
  url?: string;
  width?: number;
  height?: number;
}

class EmbedErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error('[bandcamp] Embed error:', error);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

async function fetchBandcampEmbed(artistName: string, trackName: string, bandcampUrl?: string): Promise<BandcampOEmbed> {
  try {
    const params = new URLSearchParams();
    if (bandcampUrl) {
      params.set('url', bandcampUrl);
    } else {
      params.set('artist', artistName);
      params.set('track', trackName);
    }
    const response = await fetch(`/api/v1/bandcamp/embed?${params}`);
    if (!response.ok) {
      return { found: false };
    }
    return await response.json();
  } catch (err) {
    console.error('[bandcamp] Fetch error:', err);
    return { found: false };
  }
}

function extractIframeSrc(html: string): string | null {
  const match = html.match(/src="([^"]+)"/);
  return match ? match[1] : null;
}

let embedIdCounter = 0;

function BandcampEmbedInner({ artistName, trackName, bandcampUrl, preloadedEmbed, onReady, size = 'small' }: BandcampEmbedProps) {
  const [embedData, setEmbedData] = useState<BandcampOEmbed | null>(null);
  const [loading, setLoading] = useState(!preloadedEmbed);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const embedIdRef = useRef(`bandcamp-${++embedIdCounter}`);
  const mediaPlayer = useMediaPlayerOptional();
  
  const isPlayingRef = useRef(false);
  const lastActiveElementRef = useRef<Element | null>(null);
  const isHoveredRef = useRef(false);
  const lastHadFocusRef = useRef(document.hasFocus());

  useEffect(() => {
    if (preloadedEmbed) {
      setEmbedData({
        found: true,
        html: preloadedEmbed.embedHtml,
        title: preloadedEmbed.title,
      });
      setLoading(false);
      onReady?.();
      return;
    }

    setLoading(true);
    fetchBandcampEmbed(artistName, trackName, bandcampUrl)
      .then(data => {
        setEmbedData(data);
        setLoading(false);
        onReady?.();
      })
      .catch(err => {
        console.error('[bandcamp] Embed failed:', err);
        setLoading(false);
        onReady?.();
      });
  }, [artistName, trackName, bandcampUrl, preloadedEmbed]);

  // Poll for focus changes to detect iframe clicks
  useEffect(() => {
    if (!mediaPlayer || !embedData?.found) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    const embedId = embedIdRef.current;
    const title = `${artistName} - ${trackName}`;

    const handleClick = () => {
      const scrollY = window.scrollY;
      const restore = () => window.scrollTo(0, scrollY);

      const wasPlaying = isPlayingRef.current;
      const nowPlaying = !wasPlaying;
      isPlayingRef.current = nowPlaying;
      
      if (nowPlaying) {
        mediaPlayer.registerPlaying({
          type: 'bandcamp',
          id: embedId,
          title,
          artistName,
        });
      } else {
        mediaPlayer.registerStopped(embedId);
      }

      // Pull focus back from iframe so next click can be detected
      iframe.blur();
      window.focus();

      restore();
      requestAnimationFrame(restore);
      setTimeout(restore, 50);
    };

    const checkFocus = () => {
      const activeElement = document.activeElement;
      const wasOurIframe = lastActiveElementRef.current === iframe;
      const isOurIframe = activeElement === iframe;

      // Chrome: detect when focus ENTERS our iframe via activeElement
      if (!wasOurIframe && isOurIframe) {
        handleClick();
      }

      // Safari: detect via hasFocus() + hover (activeElement doesn't work for iframes)
      const hasFocus = document.hasFocus();
      if (lastHadFocusRef.current && !hasFocus && isHoveredRef.current) {
        handleClick();
      }
      lastHadFocusRef.current = hasFocus;

      lastActiveElementRef.current = activeElement;
    };

    const intervalId = setInterval(checkFocus, 100);
    return () => clearInterval(intervalId);
  }, [mediaPlayer, embedData?.found, artistName, trackName]);

  // Register controls
  useEffect(() => {
    if (!mediaPlayer || !embedData?.found) return;

    const embedId = embedIdRef.current;

    const stopPlayback = () => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      const scrollY = window.scrollY;
      const src = iframe.src;
      iframe.src = '';
      requestAnimationFrame(() => {
        if (iframe) iframe.src = src;
      });
      
      isPlayingRef.current = false;

      const restore = () => window.scrollTo(0, scrollY);
      restore();
      requestAnimationFrame(restore);
      setTimeout(restore, 50);
    };

    mediaPlayer.registerControls(embedId, {
      pause: stopPlayback,
      play: () => {},
      stop: stopPlayback,
    });

    return () => mediaPlayer.unregisterControls(embedId);
  }, [mediaPlayer, embedData?.found]);

  const bandcampSearchUrl = `https://bandcamp.com/search?q=${encodeURIComponent(`${artistName} ${trackName}`)}`;

  if (embedData?.found && embedData.html) {
    let iframeSrc = extractIframeSrc(embedData.html);
    if (iframeSrc) {
      // Size options:
      // - small: 120px horizontal strip (play button + title)
      // - medium: 300px square (album art only, no tracklist) - safest for click detection
      // - large: 470px with tracklist (has problematic click zones)
      if (size === 'large' || size === 'medium') {
        iframeSrc = iframeSrc
          .replace(/size=small/, 'size=large')
          .replace(/size=medium/, 'size=large');
        // Always hide tracklist for medium, optional for large
        if (size === 'medium' || !iframeSrc.includes('tracklist=')) {
          iframeSrc = iframeSrc.replace(/tracklist=(true|false)/, 'tracklist=false');
          if (!iframeSrc.includes('tracklist=')) {
            iframeSrc = iframeSrc.replace(/\/$/, '/tracklist=false/');
          }
        }
      }
      if (size === 'medium') {
        // Square: crop Bandcamp's large embed to show just album art
        return (
          <div
            className={`bandcamp-embed ready ${size}`}
            onMouseEnter={() => { isHoveredRef.current = true; }}
            onMouseLeave={() => { isHoveredRef.current = false; }}
            style={{
              width: '300px',
              height: '300px',
              overflow: 'hidden',
              position: 'relative',
              margin: '0 auto',
            }}
          >
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              style={{
                border: 0,
                width: '300px',
                height: '470px',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              seamless
              title={`${artistName} - ${trackName}`}
              allow="autoplay"
            />
          </div>
        );
      }

      const height = size === 'large' ? '470px' : '120px';

      return (
        <div
          className={`bandcamp-embed ready ${size}`}
          onMouseEnter={() => { isHoveredRef.current = true; }}
          onMouseLeave={() => { isHoveredRef.current = false; }}
        >
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            style={{ border: 0, width: '100%', height }}
            seamless
            title={`${artistName} - ${trackName}`}
            allow="autoplay"
          />
        </div>
      );
    }
    return (
      <div
        className="bandcamp-embed ready"
        dangerouslySetInnerHTML={{ __html: embedData.html }}
      />
    );
  }

  return (
    <a
      href={bandcampSearchUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`bandcamp-embed fallback ${loading ? 'loading' : ''}`}
    >
      <div className="embed-placeholder">
        <div className="bandcamp-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
          </svg>
        </div>
        <div className="track-info">
          <span className="track-name">{trackName}</span>
          <span className="artist-name">{artistName}</span>
        </div>
        {loading ? (
          <span className="search-hint">Searching Bandcamp...</span>
        ) : (
          <span className="search-hint">Not on Bandcamp — tap to search</span>
        )}
      </div>
    </a>
  );
}

export function BandcampEmbed(props: BandcampEmbedProps) {
  const fallback = (
    <a
      href={`https://bandcamp.com/search?q=${encodeURIComponent(`${props.artistName} ${props.trackName}`)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="bandcamp-embed fallback"
    >
      <div className="embed-placeholder">
        <div className="bandcamp-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
          </svg>
        </div>
        <div className="track-info">
          <span className="track-name">{props.trackName}</span>
          <span className="artist-name">{props.artistName}</span>
        </div>
        <span className="search-hint">Search on Bandcamp</span>
      </div>
    </a>
  );

  return (
    <EmbedErrorBoundary fallback={fallback}>
      <BandcampEmbedInner {...props} />
    </EmbedErrorBoundary>
  );
}
